import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { logger } from '@azure/storage-blob';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
  Button,
} from '@mui/material';

import { NotificationApi } from '@/api';
import { IcClose, IcSettings } from '@/assets/images';
import { useNotificationStore, useSidebarStore } from '@/store';
import { sizes } from '@/theme/variables';

import { NotificationGroup } from './components/NotificationGroup';

const PANEL_WIDTH = 400;

export const NotificationPanel: FC = () => {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const { isOpen: sidebarOpen } = useSidebarStore();
  const {
    isPanelOpen,
    setIsPanelOpen,
    isLoading,
    setIsLoading,
    setNotifications,
    dismissAllNotifications,
    getGroupedNotifications,
  } = useNotificationStore();
  const [isClearAllConfirmOpen, setIsClearAllConfirmOpen] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);

  const groupedNotifications = getGroupedNotifications();

  // Calculate the left position based on sidebar state
  const sidebarWidth = sidebarOpen ? sizes.sidebarOpen : sizes.sidebarClosed;

  useEffect(() => {
    if (isPanelOpen) {
      fetchNotifications();
    }
  }, [isPanelOpen]);

  // Handle clicking outside the panel to dismiss it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isPanelOpen &&
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        // Check if the click is not on the notification bell button
        const notificationBell = document.querySelector(
          '[data-testid="notification-bell"]',
        );
        if (
          !notificationBell ||
          !notificationBell.contains(event.target as Node)
        ) {
          handleClose();
        }
      }
    };

    if (isPanelOpen) {
      // Add event listener with a slight delay to avoid immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPanelOpen]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await NotificationApi.getNotifications({
        sort: JSON.stringify({ createdAt: -1 }),
        limit: 50,
      });
      setNotifications(response.data.data);
    } catch (error) {
      logger.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsClearAllConfirmOpen(false);
    setIsPanelOpen(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings/notifications');
    handleClose();
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    // Only close if clicking the backdrop, not the panel content
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleNotificationClick = () => {
    handleClose();
  };

  const handleClearAllRequest = () => {
    setIsClearAllConfirmOpen(true);
  };

  const handleClearAllCancel = () => {
    setIsClearAllConfirmOpen(false);
  };

  const handleClearAllConfirm = async () => {
    setIsClearingAll(true);
    try {
      await NotificationApi.dismissAllNotifications();
      dismissAllNotifications();
      setIsClearAllConfirmOpen(false);
    } catch (error) {
      logger.error('Failed to clear all notifications:', error);
    } finally {
      setIsClearingAll(false);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={isPanelOpen}
      onClose={handleClose}
      variant="persistent"
      ModalProps={{
        keepMounted: false,
        BackdropProps: {
          onClick: handleBackdropClick,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            left: sidebarWidth,
          },
        },
      }}
      PaperProps={{
        ref: panelRef,
        sx: {
          width: PANEL_WIDTH,
          height: '100vh',
          backgroundColor: 'background.paper',
          left: sidebarWidth,
          transform: isPanelOpen
            ? 'translateX(0)'
            : `translateX(-${PANEL_WIDTH}px)`,
          transition: 'transform 0.3s ease-in-out',
          zIndex: 5,
        },
      }}
      SlideProps={{
        direction: 'right',
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Notification
          </Typography>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={handleSettingsClick}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <IcSettings width={20} height={20} />
            </IconButton>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <IcClose width={20} height={20} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : groupedNotifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No notifications
              </Typography>
              <Typography variant="body2" color="text.disabled">
                You&apos;re all caught up!
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              {groupedNotifications.map((group) => (
                <NotificationGroup
                  key={group.title}
                  group={group}
                  onNotificationClick={handleNotificationClick}
                />
              ))}
            </Box>
          )}
        </Box>

        {groupedNotifications.length > 0 && !isLoading ? (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 2,
              backgroundColor: 'background.paper',
            }}
          >
            {isClearAllConfirmOpen ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" color="text.secondary" flex={1}>
                  Are you sure?
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={handleClearAllCancel}
                  disabled={isClearingAll}
                  sx={{ minWidth: 110, height: 32, px: 0 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={handleClearAllConfirm}
                  disabled={isClearingAll}
                  sx={{ minWidth: 110, height: 32, px: 0 }}
                >
                  Clear all
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="center">
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={handleClearAllRequest}
                >
                  Clear all
                </Button>
              </Stack>
            )}
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
};
